import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import QRCode from "qrcode";
import {
  Share, ShareNetwork, X, WhatsappLogo, Envelope, Copy, XLogo,
  LinkedinLogo, FacebookLogo, TelegramLogo, CheckCircle, QrCode,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * ShareButton — universal share affordance used across NaturePulse.
 *
 * Uses the native Web Share API on capable devices; otherwise pops a small
 * portal-rendered sheet with WhatsApp / X / Facebook / LinkedIn / Telegram /
 * Email / Copy link / QR. Every outbound link is tagged `?ref=share&via=<key>`
 * so we can attribute traffic later.
 *
 * Props:
 *  - url?: string  — the URL to share. Defaults to window.location.href.
 *  - title?: string — page/post title.
 *  - text?: string  — short description that appears in previews / message body.
 *  - hashtags?: string[]  — used for X.
 *  - variant?: "icon" | "chip" | "pill"
 *  - size?: "sm" | "md"
 *  - tone?: "dark" | "light" — button skin
 *  - className?: string
 *  - testid?: string
 */
export default function ShareButton({
  url,
  title = "NaturePulse",
  text = "",
  hashtags = ["NaturePulse", "Nature"],
  variant = "chip",
  size = "sm",
  tone = "light",
  className = "",
  testid = "share-button",
  shortKind,
  shortId,
  preShortUrl,    // if the caller already has short_url from the story doc
  preQrUrl,       // and the QR endpoint too — skip the /for-page call entirely
}) {
  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState("");
  const [copied, setCopied] = useState(false);
  const [shortUrl, setShortUrl] = useState(preShortUrl || "");
  const [brandedQr, setBrandedQr] = useState(preQrUrl || "");
  const initedFor = useRef("");

  const effectiveUrl = withUtm(url || (typeof window !== "undefined" ? window.location.href : ""), "share");
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  // What actually goes into WhatsApp / SMS / QR — the short URL if we got one.
  const outboundUrl = shortUrl || effectiveUrl;

  useEffect(() => {
    if (!open) return;
    if (initedFor.current === effectiveUrl && qr) return;
    initedFor.current = effectiveUrl;

    // Local fallback QR (dark on cream) — used until the branded SpLink QR loads.
    QRCode.toDataURL(effectiveUrl, {
      margin: 1, width: 384,
      color: { dark: "#1B4332", light: "#F5EBDC" },
    }).then(setQr).catch(() => setQr(""));

    // Ask our backend to shorten + return the branded QR. If SpLink isn't
    // configured we simply keep the local QR + long URL — no error surfaced.
    // Skip if the caller already supplied a pre-attached short URL/QR.
    if (preShortUrl && preQrUrl) return;
    (async () => {
      try {
        const raw = url || (typeof window !== "undefined" ? window.location.href : "");
        if (!raw) return;
        const { default: axios } = await import("axios");
        const { IS_FARMERSPULSE } = await import("../lib/site");
        const base = process.env.REACT_APP_BACKEND_URL || "";
        // Tell backend which tenant to use (Origin can be stripped by proxies)
        const siteParam = IS_FARMERSPULSE ? "?site=farmerspulse" : "";
        const { data } = await axios.post(
          `${base}/api/short/for-page${siteParam}`,
          { kind: shortKind || "custom", id: shortId, absolute_url: raw },
          { timeout: 8000 },
        );
        if (data?.short_url) setShortUrl(data.short_url);
        if (data?.qr_url) setBrandedQr(data.qr_url);
      } catch { /* silent — the local QR still works */ }
    })();
  }, [open, effectiveUrl, qr, url, shortKind, shortId]);

  const handle = async () => {
    if (canNativeShare) {
      try {
        await navigator.share({ title, text: text || title, url: effectiveUrl });
        return;
      } catch (e) {
        // user cancelled or share failed → fall through to sheet
        if (e && e.name !== "AbortError") setOpen(true);
        return;
      }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(outboundUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Couldn't copy link."); }
  };

  // Compact trigger button — three visual variants
  const trigger = renderTrigger({ variant, size, tone, className, testid, handle });

  const sheet = open && createPortal(
    <AnimatePresence>
      <motion.div
        key="share-overlay"
        className="fixed inset-0 z-[95] bg-black/60"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={() => setOpen(false)}
      />
      <motion.div
        key="share-sheet"
        role="dialog" aria-label="Share"
        data-testid={`${testid}-sheet`}
        className="fixed z-[96] inset-x-0 bottom-0 md:inset-0 md:m-auto md:h-fit md:max-w-[460px] rounded-t-2xl md:rounded-2xl bg-[#F5EBDC] text-[#1B4332] p-5 shadow-2xl"
        initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
        transition={{ type: "spring", stiffness: 220, damping: 26 }}
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 20px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">/ Share</div>
            <div className="font-serif-display text-xl mt-0.5 leading-tight line-clamp-2">{title}</div>
            {text && <div className="mt-1 text-sm text-[#3A3830] line-clamp-2">{text}</div>}
          </div>
          <button
            onClick={() => setOpen(false)}
            data-testid={`${testid}-close`}
            className="shrink-0 rounded-full p-1 hover:bg-black/5"
            aria-label="Close"
          ><X size={16} weight="bold" /></button>
        </div>

        {/* Channel grid */}
        <div className="mt-4 grid grid-cols-4 gap-2">
          <ShareChip Icon={WhatsappLogo} label="WhatsApp"
            href={`https://api.whatsapp.com/send?text=${encodeURIComponent(`${text || title} — ${withUtm(outboundUrl, "share", "whatsapp")}`)}`}
            testid={`${testid}-whatsapp`}
          />
          <ShareChip Icon={XLogo} label="X"
            href={`https://x.com/intent/tweet?url=${encodeURIComponent(withUtm(outboundUrl, "share", "x"))}&text=${encodeURIComponent(text || title)}${hashtags?.length ? `&hashtags=${encodeURIComponent(hashtags.join(","))}` : ""}`}
            testid={`${testid}-x`}
          />
          <ShareChip Icon={FacebookLogo} label="Facebook"
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(withUtm(outboundUrl, "share", "facebook"))}`}
            testid={`${testid}-facebook`}
          />
          <ShareChip Icon={LinkedinLogo} label="LinkedIn"
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withUtm(outboundUrl, "share", "linkedin"))}`}
            testid={`${testid}-linkedin`}
          />
          <ShareChip Icon={TelegramLogo} label="Telegram"
            href={`https://t.me/share/url?url=${encodeURIComponent(withUtm(outboundUrl, "share", "telegram"))}&text=${encodeURIComponent(text || title)}`}
            testid={`${testid}-telegram`}
          />
          <ShareChip Icon={Envelope} label="Email"
            href={`mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text ? text + "\n\n" : ""}${withUtm(outboundUrl, "share", "email")}`)}`}
            testid={`${testid}-email`}
          />
          <ShareChip Icon={copied ? CheckCircle : Copy} label={copied ? "Copied" : "Copy link"}
            onClick={copy} testid={`${testid}-copy`}
          />
          {canNativeShare && (
            <ShareChip Icon={Share} label="More…" onClick={handle} testid={`${testid}-native`} />
          )}
        </div>

        {/* QR — branded SpLink PNG when available, fallback to local QR */}
        <div className="mt-4 rounded-xl bg-white/70 border border-black/10 p-3 flex items-center gap-3">
          <div className="w-[92px] h-[92px] shrink-0 rounded-lg overflow-hidden bg-[#F5EBDC] grid place-items-center">
            {brandedQr ? (
              <img src={brandedQr} width="92" height="92" alt="Scan to open" data-testid={`${testid}-qr`} />
            ) : qr ? (
              <img src={qr} width="92" height="92" alt="Scan to open" data-testid={`${testid}-qr`} />
            ) : <QrCode size={22} className="text-[color:var(--np-muted,#3A3830)]" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {shortUrl ? "Short link · scan to open" : "Scan to open"}
            </div>
            <div className="mt-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] break-all" data-testid={`${testid}-short-url`}>
              {shorten(shortUrl || effectiveUrl)}
            </div>
            {shortUrl && (
              <button
                onClick={() => { navigator.clipboard?.writeText(shortUrl); toast.success("Short link copied"); }}
                data-testid={`${testid}-short-copy`}
                className="mt-1.5 inline-flex items-center gap-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:opacity-80"
              >
                <Copy size={10} weight="bold" /> Copy short link
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );

  return (<>{trigger}{sheet}</>);
}

// --------- helpers ---------
function withUtm(url, source, medium) {
  try {
    const u = new URL(url);
    if (!u.searchParams.get("ref")) u.searchParams.set("ref", source);
    if (medium && !u.searchParams.get("via")) u.searchParams.set("via", medium);
    return u.toString();
  } catch { return url; }
}

function shorten(url) {
  if (!url) return "";
  return url.length > 60 ? url.slice(0, 34) + "…" + url.slice(-16) : url;
}

function ShareChip({ Icon, label, href, onClick, testid }) {
  const Cls = "flex flex-col items-center gap-1 rounded-lg border border-black/10 bg-white hover:bg-white/90 px-2 py-3 text-center transition-colors";
  const inner = (
    <>
      <Icon size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" />
      <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#1B4332] leading-none">{label}</span>
    </>
  );
  if (href) {
    return <a href={href} target="_blank" rel="noreferrer" className={Cls} data-testid={testid}>{inner}</a>;
  }
  return <button onClick={onClick} className={Cls} data-testid={testid}>{inner}</button>;
}

function renderTrigger({ variant, size, tone, className, testid, handle }) {
  const iconSize = size === "md" ? 16 : 13;
  const brandLight = "border border-[color:var(--np-brand,#B85F2A)] text-[color:var(--np-brand,#B85F2A)] hover:bg-[color:var(--np-brand,#B85F2A)] hover:text-white";
  const brandDark = "border border-[#F5EBDC]/30 text-[#F5EBDC] hover:bg-[#F5EBDC] hover:text-[#1B4332]";
  const skin = tone === "dark" ? brandDark : brandLight;
  const base = "inline-flex items-center gap-1.5 font-mono-tech tracking-[0.28em] uppercase transition-colors";

  if (variant === "icon") {
    return (
      <button
        onClick={handle}
        aria-label="Share"
        data-testid={testid}
        className={`inline-flex items-center justify-center rounded-full ${size === "md" ? "w-9 h-9" : "w-8 h-8"} ${skin} ${className}`}
      >
        <ShareNetwork size={iconSize} weight="bold" />
      </button>
    );
  }
  if (variant === "pill") {
    return (
      <button
        onClick={handle}
        data-testid={testid}
        className={`${base} rounded-full ${size === "md" ? "px-4 py-2 text-[10px]" : "px-3 py-1.5 text-[9px]"} ${skin} ${className}`}
      >
        <ShareNetwork size={iconSize} weight="bold" /> Share
      </button>
    );
  }
  // "chip" default
  return (
    <button
      onClick={handle}
      data-testid={testid}
      className={`${base} rounded-full ${size === "md" ? "px-4 py-2 text-[10px]" : "px-3 py-1 text-[9px]"} ${skin} ${className}`}
    >
      <ShareNetwork size={iconSize} weight="bold" /> Share
    </button>
  );
}
